import Guid from '_shared/guids/guid';
import CommunicationsAccessSchema, {
  CommunicationsAccessJSON,
  CommunicationsAccessParams
} from 'permissions/resources/messaging/_schemas/communications-access-schema';
import ForumAccessSchema, { ForumAccessParams, ForumAccessJSON } from 'permissions/resources/messaging/_schemas/forum-access-schema';
import { Resource } from 'permissions/resources/permission-resource';
import { registerResource } from 'permissions/resources/resource-registry';
import {
  AccessLevel,
  AnyResourceJSON,
  ResourceFromJSON,
  ResourceType,
  Scope
} from 'permissions/resources/permission-resource-types';

export class CommunicationsAccess extends Resource {
  public static create(params?: CommunicationsAccessParams): CommunicationsAccess {
    const parsed = CommunicationsAccessSchema.validate(params);
    return new CommunicationsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = CommunicationsAccessSchema.deserialize(json as CommunicationsAccessJSON);
    return new CommunicationsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): CommunicationsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(CommunicationsAccess, fieldIterations) as CommunicationsAccess[];
  }

  public readonly resourceType = ResourceType.CommunicationsAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: CommunicationsAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): CommunicationsAccessJSON {
    return CommunicationsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `CommunicationsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("CommunicationsAccess", (json) => CommunicationsAccess.fromJSON(json));

export class ForumAccess extends Resource {
  public static create(params?: ForumAccessParams): ForumAccess {
    const parsed = ForumAccessSchema.validate(params);
    return new ForumAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = ForumAccessSchema.deserialize(json as ForumAccessJSON);
    return new ForumAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.ForumAccess;
  public readonly forumId?: Guid;
  public readonly accessLevel?: AccessLevel;

  public constructor(params?: Omit<ForumAccessParams, 'resourceType'>) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.forumId = params.forumId;
    }
  }

  public toJSON(): ForumAccessJSON {
    return ForumAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `ForumAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      forumId: ${this.forumId}
    }`
  }
}

registerResource("ForumAccess", (json) => ForumAccess.fromJSON(json));
